import { PayloadAction } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { StatusType } from '@enums';
import {
  addSnack,
  logOut,
  setAppLoading,
  systemError,
  systemUpdateNetworkConnection,
  systemUpdatePageFocus,
  updateApiConnectionStatus,
  updateToken,
  updateTokenSuccess,
} from '@store/slices';
import { tokenRefreshManager } from '@utils';

export function* checkAndRefreshToken(tickCount?: number) {
  // tickCount parameter kept for backward compatibility but not used
  try {
    const refreshSuccess = yield call([tokenRefreshManager, 'refreshTokenIfNeeded']);
    if (!refreshSuccess) {
      yield put(logOut());
      return 'failed';
    }
    return refreshSuccess ? 'refreshed' : 'valid';
  } catch (error) {
    yield put(logOut());
    return 'failed';
  }
}

function* updateNetworkConnectionSaga() {
  const isBackOnline = yield select((state) => state.system.networkConnection.backOnline);
  try {
    yield put(
      addSnack({
        type: isBackOnline ? StatusType.Success : StatusType.Error,
        message: i18n.t(isBackOnline ? 'common:connection.restored' : 'common:connection.disconnected'),
        clear: isBackOnline,
      }),
    );
    if (isBackOnline) {
      yield put(systemUpdatePageFocus({ isActive: true }));
    }
  } catch (error) {
    yield put(systemError(error instanceof Error ? error : new Error('Unknown error')));
  }
}

function* checkTokenAfterInactivitySaga({ payload: { isActive } }: PayloadAction<{ isActive: boolean }>) {
  if (isActive) {
    yield put(setAppLoading(true));
    try {
      // Use the centralized token refresh manager
      const refreshSuccess = yield call([tokenRefreshManager, 'refreshTokenIfNeeded']);
      if (!refreshSuccess) {
        yield put(logOut());
      }
    } catch (e) {
      yield put(logOut());
    } finally {
      yield put(setAppLoading(false));
    }
  }
}

function* updateTokenSaga() {
  // The actual token refresh is handled by tokenRefreshManager
  // This saga just serves as a bridge for the Redux action
  // The tokenRefreshManager.performTokenRefresh will dispatch updateTokenSuccess when done
}

function* updateTokenSuccessSaga() {
  const isAPIWorking = yield select((state) => state.system.apiConnection.isConnected);
  if (!isAPIWorking) {
    yield put(updateApiConnectionStatus({ status: true }));
  }
}

export function* systemSagaWatcher() {
  yield takeLatest(systemUpdatePageFocus.type, checkTokenAfterInactivitySaga);
  yield takeLatest(systemUpdateNetworkConnection.type, updateNetworkConnectionSaga);

  yield takeLatest(updateToken.type, updateTokenSaga);
  yield takeLatest(updateTokenSuccess.type, updateTokenSuccessSaga);
}
